/**
 * Rounding that agrees with the producer, for figures the producer already decided.
 *
 * The producer rounds every printed figure once, half away from zero on the
 * shortest decimal that reads back as the number (`figure_round.round_half_up`):
 * `62.5 -> 63`, `2.25 -> 2.3`, `-0.5 -> -1`, as a reader and `Intl.NumberFormat`
 * round. JavaScript's `toFixed` rounds the binary value, so `2.675` (stored just
 * under) prints `2.67`; this rule reads it as written and prints `2.68`.
 *
 * TypeScript derives nothing the producer already decided, so where a figure must
 * be rounded for display here, it is rounded by the producer's rule. This is a
 * rounding RULE, not a precision preference: how many decimal places a surface
 * prints stays wherever it was decided.
 */

/** `value` as the shortest decimal that reads back as it, scaled by `10^decimals`. */
function scaledAsWritten(value: number, decimals: number): number {
  // 15 significant digits drop the binary noise a product picks up (2.675 * 100 is
  // 267.49999999999997) and keep every digit the number was written with.
  return Number((Math.abs(value) * 10 ** decimals).toPrecision(15))
}

/**
 * `value` rounded to a whole number, half away from zero (`62.5 -> 63`,
 * `-62.5 -> -63`). Non-finite input is returned unchanged rather than coerced, so a
 * caller's own `Number.isFinite` guard still sees what it was given.
 */
export function roundHalfAwayFromZero(value: number): number {
  return roundHalfAwayFromZeroAt(value, 0)
}

/** `value` rounded to `decimals` places, half away from zero, read as written. */
export function roundHalfAwayFromZeroAt(value: number, decimals: number): number {
  if (!Number.isFinite(value)) {
    return value
  }
  const places = Math.max(0, Math.trunc(decimals))
  const rounded = Math.floor(scaledAsWritten(value, places) + 0.5) / 10 ** places
  // `+ 0` turns a rounded -0 into 0, so nothing prints "-0".
  return (value < 0 ? -rounded : rounded) + 0
}
